import { useState, type ReactElement } from 'react';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import {
  formatRangeWindowNoDataText,
  resolveQualityComparisonWindowLabel,
  useQualityDateRange,
} from '@/features/quality/hooks';
import { useReleasesPageViewModel } from '@/features/releases/hooks/useReleasesPageViewModel';
import { ReleasesTablePanel } from '@/features/releases/pages/ReleasesTablePanel';
import { resolveGenericStatusCardIcon } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection.icons';
import { MetricsPageToolbar } from '@/features/shared/components/metricsToolbar/MetricsPageToolbar';
import { TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { releaseStatusOptions } from '@/shared/constants/statusOptions';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { r, s } from '@/theme/tokens';
import { ListPageTemplate } from '@/ui/composites/scaffolds';
import { Box, ReleasesIcon, Text } from '@/ui/primitives';
import type { ReleaseStatus } from '@flowqa/shared';

const resolveReleasesStatusCardIcon = (statusId: string): typeof ReleasesIcon => {
  if (statusId === 'total') return ReleasesIcon;
  return resolveGenericStatusCardIcon(statusId);
};

export const ReleasesPage = (): ReactElement => {
  const {
    range,
    setRange,
    selectedStatuses,
    selectedTags,
    selectedProjects,
    tagOptions,
    projectFilterOptions,
    showProjectFilter,
    onStatusToggle,
    onStatusesChange,
    onTagsChange,
    onProjectsChange,
    tableFilters,
    releaseMetricCards,
  } = useReleasesPageViewModel();
  const [searchValue, setSearchValue] = useState('');
  const { rangeStartDay, rangeEndDay } = useQualityDateRange(range);
  const comparisonWindowLabel = resolveQualityComparisonWindowLabel(range);
  const tableNoRowsText = formatRangeWindowNoDataText(
    getEmptyStateMessage('entity.noneFound', { entity: 'release' }),
    range,
    false,
  );

  return (
    <ListPageTemplate
      headerActionsMode="separate"
      title={
        <Text role="pageTitle" component="h1">
          Releases
        </Text>
      }
      headerActions={
        <Box
          sx={{
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: r('card'),
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            display: 'flex',
            alignItems: 'center',
            px: s('md'),
            width: '100%',
          }}
        >
          <MetricsPageToolbar
            title=""
            showInlineFilters
            searchAriaLabel="Search releases"
            searchValue={searchValue}
            onDebouncedSearchChange={setSearchValue}
            filterStatuses={selectedStatuses}
            statusOptions={releaseStatusOptions}
            onFilterStatusesChange={(next) => {
              onStatusesChange(next as ReleaseStatus[]);
            }}
            filterTags={selectedTags}
            onFilterTagsChange={onTagsChange}
            filterProjects={showProjectFilter ? selectedProjects : undefined}
            onFilterProjectsChange={showProjectFilter ? onProjectsChange : undefined}
            projectOptions={showProjectFilter ? projectFilterOptions : undefined}
            dateQuickFilter={range === 'all' ? '30d' : range}
            onDateQuickFilterChange={(next) => {
              setRange(next);
            }}
            fromDate={null}
            toDate={null}
            latestQueryStartDay={rangeStartDay}
            latestQueryEndDay={rangeEndDay}
            onFromDateChange={() => undefined}
            onToDateChange={() => undefined}
            availableTags={tagOptions.map((option) => option.value)}
            showDateControls
          />
        </Box>
      }
      summary={
        <StatusMetricCardsRow
          cards={releaseMetricCards}
          selectedIds={selectedStatuses}
          comparisonWindowLabel={comparisonWindowLabel}
          resolveStatusCardIcon={resolveReleasesStatusCardIcon}
          onToggle={(status) => {
            if (status === 'total') return;
            onStatusToggle(status as ReleaseStatus);
          }}
        />
      }
      content={
        <ReleasesTablePanel
          fixedFilters={tableFilters}
          rangeStartDay={rangeStartDay}
          rangeEndDay={rangeEndDay}
          noRowsText={tableNoRowsText}
          searchValue={searchValue}
        />
      }
      paddingBottom="lg"
    />
  );
};
